export const areRectsOverlapping = (
  a: { x: number, y: number, width: number, height: number },
  b: { x: number, y: number, width: number, height: number }
): boolean => {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  )
}

export const validateRect = rect => {
  const cloneRect = { ...rect };

  if (cloneRect.width < 0) {
    cloneRect.x = cloneRect.x + cloneRect.width;
    cloneRect.width = Math.abs(cloneRect.width);
  }
  if (cloneRect.height < 0) {
    cloneRect.y = cloneRect.y + cloneRect.height;
    cloneRect.height = Math.abs(cloneRect.height);
  }
  if (cloneRect.x < 0) {
    cloneRect.x = 0;
  }
  if (cloneRect.y < 0) {
    cloneRect.y = 0;
  }

  const minDimension = 10;
  const isLastRectTooSmall = cloneRect.width < minDimension || cloneRect.height < minDimension;

  return isLastRectTooSmall ? null : cloneRect;
};

export const getStageDimensions = containerRef => {
  const result = { width: 100, height: 100 };
  if (containerRef && containerRef.current) {
    result.width = containerRef.current.offsetWidth;
    result.height = containerRef.current.offsetHeight;
  }
  return result;
};

export const clickedOnEmpty = data => {
  return data.target === data.target.getStage();
};

export const getProductsFromSession = () => {
  const preloadProducts = sessionStorage.getItem('preloadProducts')
  return preloadProducts ? JSON.parse(preloadProducts) : {}
}

export const generatePrompt = ():string => {
  const preloadProductsObj = getProductsFromSession()
  const categories = Object.keys(preloadProductsObj)

  if (categories.length === 0) {
    return 'Find all objects in this image. Return results as a JSON array in this format: [{"object_name": "object-name", "object_location": [x, y, width, height]}].'
  }

  const categoriesList = categories.join(', ')
  const categoriesArray = categories.map(cat => `"${cat}"`).join(', ')

  // eslint-disable-next-line max-len
  return `Analyze this image and detect all objects that match these EXACT categories: ${categoriesList}

VALID CATEGORY NAMES (USE ONLY THESE - NO MODIFICATIONS ALLOWED):
[${categoriesArray}]

CRITICAL RULE - EXACT CATEGORY NAME MATCHING:
- You MUST use ONLY the exact category names from the list above
- DO NOT add prefixes, suffixes, or modifiers to category names
- DO NOT create variations like "gold-necklace" if the category is "necklace"
- DO NOT create variations like "yellow-jewelry" if the category is "necklace"
- DO NOT add color descriptors unless the category name already includes them
- Examples of WRONG usage:
  * If category is "necklace" → WRONG: "gold-necklace", "yellow-necklace", "gold-jewelry" → CORRECT: "necklace"
  * If category is "shirt" → WRONG: "blue-shirt", "red-shirt" → CORRECT: "shirt" (unless "blue-shirt" is in the list)
  * If category is "bag" → WRONG: "red-bag", "leather-bag" → CORRECT: "bag"
- If you see a necklace, and the category list contains "necklace", use "necklace" - NOT "gold-necklace" or any variation
- Match the object to the closest category from the list, then use that EXACT category name

DETECTION INSTRUCTIONS:
1. Scan the entire image systematically - divide into a grid and check each area from top-left to bottom-right
2. For each object you find, match it to the CLOSEST category from the valid list above
3. Once you identify the matching category, use its EXACT name (copy it exactly as shown in the list)
4. Consider color variations due to lighting - colors appear lighter in bright areas and darker in shadows
5. Scan both foreground and background - check people, objects, furniture, decorations, and all visible items
6. Only detect objects where you are confident (70%+ certainty) they match a category from the list
7. Create tight bounding boxes around each detected object

COLOR GUIDANCE:
- Account for lighting: bright areas make colors appear lighter, shadows make them darker
- If a category name includes a color (e.g., "blue-shirt"), match that specific color
- If a category name does NOT include a color (e.g., "necklace"), ignore the object's color and use the base category name
- If uncertain between similar categories, choose the one that best matches from the list

BOUNDING BOX FORMAT:
- Use format [x, y, width, height] where:
  - x = horizontal position from left edge (0 = leftmost)
  - y = vertical position from top edge (0 = topmost)  
  - width = horizontal span of the object
  - height = vertical span of the object
- Make boxes tight around the object edges
- For clothing: box the visible garment area (e.g., shirt from shoulders to waist, pants from waist to hem)
- For objects: box the entire visible object including any frames or borders

OUTPUT FORMAT:
Return a JSON array with all detected objects.
Format: [{"object_name": "exact-category-name", "object_location": [x, y, width, height]}, ...]

FINAL VALIDATION BEFORE RETURNING:
Before returning your response, verify:
1. Every object_name in your response appears EXACTLY in the valid category list above
2. No object_name has been modified, prefixed, or suffixed
3. If you detected a necklace and the list has "necklace", you used "necklace" (not "gold-necklace")
4. All category names match character-for-character with the list above

If no objects match any category from the list, return an empty array [].`
}

/**
 * Convert string to title case
 */
export function toTitleCase (str:string):string {
  return str.replace(/\w\S*/g, (txt) =>
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  )
}

/**
 * Capitalize first letter only
 */
export function capitalize (str:string):string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Convert to camelCase or PascalCase
 */
export function toCamelCase (text:string, firstUpper:boolean = false):string {
  const words = text.replace(/[-_]/g, ' ').split(/\s+/).filter(word => word.length > 0)
  if (words.length === 0) return text

  let result = firstUpper ? capitalize(words[0]) : words[0].toLowerCase()
  for (let i = 1; i < words.length; i++) {
    result += capitalize(words[i])
  }
  return result
}

/**
 * Create alternating case pattern
 */
export function alternatingCase (text:string, startUpper:boolean = true):string {
  let result = ''
  let upper = startUpper

  for (const char of text) {
    if (/[a-zA-Z]/.test(char)) {
      result += upper ? char.toUpperCase() : char.toLowerCase()
      upper = !upper
    } else {
      result += char
    }
  }
  return result
}

export function generateStringVariations (inputString:string):string[] {
  const variations = new Set()

  // Original string
  variations.add(inputString)

  // Basic case variations
  variations.add(inputString.toLowerCase())
  variations.add(inputString.toUpperCase())
  variations.add(toTitleCase(inputString))
  variations.add(capitalize(inputString))

  // Remove common separators (hyphens, underscores, spaces)
  const noSeparators = inputString.replace(/[-_\s]/g, '')
  variations.add(noSeparators)
  variations.add(noSeparators.toLowerCase())
  variations.add(noSeparators.toUpperCase())
  variations.add(toTitleCase(noSeparators))
  variations.add(capitalize(noSeparators))

  // Replace separators with other common separators
  const separators = ['-', '_', ' ', '.']
  separators.forEach(sep => {
    if (!inputString.includes(sep)) { // Only if not already present
      const temp = inputString.replace(/[-_\s]/g, sep)
      variations.add(temp)
      variations.add(temp.toLowerCase())
      variations.add(temp.toUpperCase())
      variations.add(toTitleCase(temp))
    }
  })

  // CamelCase variations
  variations.add(toCamelCase(inputString, false)) // camelCase
  variations.add(toCamelCase(inputString, true)) // PascalCase

  // Snake_case variation
  const snakeCase = inputString.replace(/[-\s]/g, '_').toLowerCase()
  variations.add(snakeCase)
  variations.add(snakeCase.toUpperCase()) // SCREAMING_SNAKE_CASE

  // Kebab-case variation
  const kebabCase = inputString.replace(/[_\s]/g, '-').toLowerCase()
  variations.add(kebabCase)
  variations.add(kebabCase.toUpperCase())

  // Mixed case variations (alternating)
  variations.add(alternatingCase(inputString, true))
  variations.add(alternatingCase(inputString, false))

  // Convert Set to Array and sort
  return Array.from(variations).sort() as string[]
}
